import { AiOutlineLoading3Quarters } from "solid-icons/ai";
import {
  TbFillCircleCheck,
  TbOutlineArrowRight,
  TbOutlineBinoculars,
  TbOutlineCheck,
} from "solid-icons/tb";
import { createEffect, For, type ParentProps, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { BuildLogState } from "@/domains/auth/onboarding/utils/build-choreography";
import { FreelanceBusinessCard } from "@/domains/freelance/components/business-card/FreelanceBusinessCard";
import type { FreelanceBusinessCardData } from "@/domains/freelance/components/business-card/types";

/** Drives both the rendered log and the choreography's log count. */
export const ONBOARDING_BUILD_LOG_KEYS = [
  "onboarding.success.building.log_1",
  "onboarding.success.building.log_2",
  "onboarding.success.building.log_3",
] as const;

export type OnboardingSuccessScreenProps = {
  data: FreelanceBusinessCardData;
  revealedSlots: number;
  logStates: BuildLogState[];
  /** True once the API confirmed *and* the choreography completed. */
  succeeded: boolean;
  animated: boolean;
  onGoToProfile: () => void;
  onExploreOffers: () => void;
  /**
   * Set when the user arrived here on the way to somewhere specific — today,
   * an offer they tried to apply to before they had an account (I11/EP7).
   *
   * It takes the primary slot: what they came to do beats what we suggest.
   * "Esplora le offerte" steps aside in that case rather than becoming a third
   * button, because next to «torna a *quella* offerta» a generic browse CTA is
   * noise.
   */
  onReturnToDestination?: () => void;
};

/** Staggered entrance used by the success copy, mirroring the prototype. */
const Reveal = (
  props: ParentProps<{ delay: number; animated: boolean; class?: string }>,
) => (
  <div
    class={props.class}
    classList={{
      "animate-fade-in-up [animation-fill-mode:both]": props.animated,
    }}
    style={
      props.animated ? { "animation-delay": `${props.delay}s` } : undefined
    }
  >
    {props.children}
  </div>
);

const BuildLogItem = (props: {
  label: string;
  state: BuildLogState;
  animated: boolean;
}) => (
  <li
    class="flex items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium"
    classList={{
      "transition-[opacity,transform,box-shadow,background-color,border-color,color] duration-300 ease-out":
        props.animated,
      "opacity-45 scale-[0.97] text-muted-foreground": props.state === "idle",
      "opacity-100 scale-[1.03] bg-card border-border text-foreground shadow-[0_10px_28px_-10px_hsl(222.2_84%_4.9%/0.16)]":
        props.state === "active",
      "opacity-100 bg-card border-primary text-foreground":
        props.state === "done",
    }}
  >
    <span
      class="grid size-7 shrink-0 place-items-center rounded-full"
      classList={{
        "bg-muted": props.state === "idle",
        "bg-primary/10 text-primary": props.state !== "idle",
      }}
    >
      <Show
        when={props.state === "done"}
        fallback={
          <AiOutlineLoading3Quarters
            class="size-3.5"
            classList={{ "animate-spin": props.state === "active" }}
            aria-hidden="true"
          />
        }
      >
        <TbOutlineCheck class="size-4" aria-hidden="true" />
      </Show>
    </span>
    {props.label}
  </li>
);

/**
 * The freelance onboarding finale: the business card builds itself from the
 * data the user just entered, then comes alive once the profile is live.
 *
 * Both states share the same grid cell so the swap causes no layout shift.
 */
export const OnboardingSuccessScreen = (
  props: OnboardingSuccessScreenProps,
) => {
  const { t } = useI18n();
  let primaryCtaRef: HTMLButtonElement | undefined;

  // Move focus onto the primary CTA when the celebration lands, so keyboard
  // and screen-reader users are taken to the next action.
  createEffect(() => {
    if (props.succeeded) {
      primaryCtaRef?.focus();
    }
  });

  return (
    <div class="grid min-h-[calc(100vh-5rem)] place-items-center p-4">
      <div class="grid w-full max-w-sm items-center gap-8 lg:max-w-4xl lg:grid-cols-2 lg:gap-14">
        <section class="order-2 grid lg:order-1">
          {/* Building state */}
          <div
            class="[grid-area:1/1] flex flex-col gap-5"
            classList={{
              "transition-[opacity,transform] duration-[400ms] ease-out":
                props.animated,
              "opacity-0 translate-y-2.5 pointer-events-none": props.succeeded,
            }}
            aria-hidden={props.succeeded ? "true" : undefined}
          >
            <div class="flex flex-col gap-2">
              <h1 class="font-display text-2xl font-bold leading-tight sm:text-3xl">
                {t("onboarding.success.building.title")}
              </h1>
              <p class="text-sm text-muted-foreground">
                {t("onboarding.success.building.subtitle")}
              </p>
            </div>
            <ul class="flex flex-col gap-2" aria-live="polite">
              <For each={ONBOARDING_BUILD_LOG_KEYS}>
                {(key, index) => (
                  <BuildLogItem
                    label={t(key)}
                    state={props.logStates[index()] ?? "idle"}
                    animated={props.animated}
                  />
                )}
              </For>
            </ul>
          </div>

          {/* Success state — mounted only on success so the staggered reveal
              plays at the swap rather than silently at page load. */}
          <div class="[grid-area:1/1] flex flex-col gap-6">
            <Show when={props.succeeded}>
              <div class="flex flex-col gap-2" role="status">
                <Reveal delay={0.1} animated={props.animated}>
                  <Badge class="w-fit gap-1.5 border-transparent bg-green-100 px-2.5 text-green-800">
                    <TbFillCircleCheck aria-hidden="true" />
                    {t("onboarding.success.badge")}
                  </Badge>
                </Reveal>
                <Reveal
                  delay={0.2}
                  animated={props.animated}
                  class="font-display text-2xl font-bold leading-tight sm:text-3xl"
                >
                  <h1>{t("onboarding.success.title")}</h1>
                </Reveal>
                <Reveal
                  delay={0.35}
                  animated={props.animated}
                  class="text-sm text-muted-foreground"
                >
                  {t("onboarding.success.subtitle")}
                </Reveal>
              </div>
              <Reveal
                delay={0.5}
                animated={props.animated}
                class="flex flex-col gap-3 sm:flex-row"
              >
                <Show
                  when={props.onReturnToDestination}
                  fallback={
                    <>
                      <Button ref={primaryCtaRef} onClick={props.onGoToProfile}>
                        {t("onboarding.success.cta_profile")}
                        <TbOutlineArrowRight class="ml-2" aria-hidden="true" />
                      </Button>
                      <Button variant="outline" onClick={props.onExploreOffers}>
                        <TbOutlineBinoculars class="mr-2" aria-hidden="true" />
                        {t("onboarding.success.cta_offers")}
                      </Button>
                    </>
                  }
                >
                  {(onReturn) => (
                    <>
                      <Button ref={primaryCtaRef} onClick={() => onReturn()()}>
                        {t("onboarding.success.cta_return")}
                        <TbOutlineArrowRight class="ml-2" aria-hidden="true" />
                      </Button>
                      <Button variant="outline" onClick={props.onGoToProfile}>
                        {t("onboarding.success.cta_profile")}
                      </Button>
                    </>
                  )}
                </Show>
              </Reveal>
            </Show>
          </div>
        </section>

        <section class="order-1 lg:order-2">
          <FreelanceBusinessCard
            class="mx-auto"
            data={props.data}
            revealedSlots={props.revealedSlots}
            live={props.succeeded}
            animated={props.animated}
          />
        </section>
      </div>
    </div>
  );
};
